<template lang="pug">
div
  slot(:items="items" :hasNext="pageInfo.hasNextPage" :isFetching="isFetching")

  .u-textCenter(style="width: 100%;")
    .u-textCenter.u-mt-md.u-mb-md(v-show="pageInfo.hasNextPage && moreButton")
      button(class="button-link is-medium" :title="$t('showMore')" v-load="isFetching" @click="fetchNext") {{ $t('showMore') }}
</template>

<script>
import { load } from '~/directives';
// This component is only used in paginated queries, do not use for non-paginated queries.
export default {
  name: 'Query',
  directives: {
    load
  },
  props: {
    query: {
      type: Object,
      required: true
    },
    refetchOnRouteChange: {
      type: Boolean,
      default: false
    },
    variables: {
      type: [Object, Function],
      default: () => y => y
    },
    limit: {
      type: Number,
      default: 10
    },
    initialValue: {
      type: Object,
      default: null
    },
    moreButton: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    items: [],
    isFetching: false,
    pageInfo: {
      hasNextPage: false,
      endCursor: null
    }
  }),
  watch: {
    $route() {
      if (this.refetchOnRouteChange) {
        this.items = [];
        this.pageInfo = {
          hasNextPage: false,
          endCursor: null
        };

        setTimeout(() => {
          this.fetchNext();
        }, 400);
      }
    },
    variables: {
      deep: true,
      handler(value) {
        const stringified = JSON.stringify(value);
        if (stringified === this._cachedVars) {
          return;
        }

        // reset the cursor.
        this.pageInfo.endCursor = null;
        this._cachedVars = stringified;
        this.items = [];
        this.fetchNext();
      }
    }
  },
  created() {
    if (!this.initialValue) {
      return;
    }

    // Ensure no accidental re-fetching due to serverside prefetching.
    this._cachedVars = JSON.stringify(this.variables);
    this.setItems(this.initialValue);
  },
  mounted() {
    if (this.initialValue) {
      return;
    }

    this.fetchNext();
  },
  methods: {
    setItems(collection) {
      if (!collection.edges) {
        return;
      }
      this.items = Object.freeze([...this.items, ...collection.edges.map(e => e.node)]);
      this.pageInfo = collection.pageInfo;
    },
    normalizeVars() {
      const vars = {
        after: this.pageInfo.endCursor,
        first: this.limit
      };

      if (typeof this.variables === 'function') {
        return this.variables(vars);
      }

      if (this.variables && typeof this.variables === 'object') {
        return {
          ...vars,
          ...this.variables
        };
      }

      return vars;
    },
    findConnectionObj(data) {
      let connection = data[Object.keys(data)[0]];
      if (connection.pageInfo) {
        return connection;
      }

      return this.findConnectionObj(connection);
    },
    async fetchNext() {
      try {
        this.isFetching = true;
        const { data } = await this.$api.query({
          query: this.query,
          variables: this.normalizeVars()
        });

        // extract the first key of the query result.
        // this limits this component's ability to only fetch root level single query results.
        const collection = this.findConnectionObj(data);
        this.setItems(collection);
        this.$emit('fetch');
      } catch (err) {
        console.log('Failed to fetch items');
        console.error(err.message);
      } finally {
        this.isFetching = false;
      }
    }
  }
};
</script>

<i18n>
{
  "en": {
    "showMore": "View More"
  },
  "ar": {
    "showMore": "عرض المزيد"
  }
}
</i18n>

<style>
.fullWidth {
  width: 100%;
}
</style>
